import {useEffect, useRef, useState, useCallback} from 'react'
import {toSseEventName} from '@/types/background-jobs'
import type {ExecutionEventType} from '@/types/background-jobs'
import {useI18n} from '@/i18n'

const MAX_RECONNECT_ATTEMPTS = 5
const INITIAL_RECONNECT_DELAY = 1000

export interface EventSourceConnectionOptions {
    url: string
    enabled?: boolean
    eventTypes: readonly ExecutionEventType[]
    onEvent: (data: unknown) => void
}

export interface EventSourceConnectionResult {
    isConnected: boolean
    error: string | null
    disconnect: () => void
}

export function useEventSourceConnection({
                                             url,
                                             enabled = true,
                                             eventTypes,
                                             onEvent,
                                         }: EventSourceConnectionOptions): EventSourceConnectionResult {
    const i18n = useI18n()
    const [isConnected, setIsConnected] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const eventSourceRef = useRef<EventSource | null>(null)
    const reconnectAttemptRef = useRef(0)
    const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
    const hasConnectedRef = useRef(false)

    const disconnect = useCallback(() => {
        if (reconnectTimeoutRef.current) {
            clearTimeout(reconnectTimeoutRef.current)
            reconnectTimeoutRef.current = null
        }
        if (eventSourceRef.current) {
            eventSourceRef.current.close()
            eventSourceRef.current = null
        }
        setIsConnected(false)
    }, [])

    const connect = useCallback(() => {
        if (!enabled) return

        if (eventSourceRef.current) {
            eventSourceRef.current.close()
        }

        const eventSource = new EventSource(url)
        eventSourceRef.current = eventSource

        eventSource.onopen = () => {
            setIsConnected(true)
            setError(null)
            reconnectAttemptRef.current = 0
            hasConnectedRef.current = true
        }

        eventSource.onerror = () => {
            setIsConnected(false)
            eventSource.close()
            eventSourceRef.current = null

            if (reconnectAttemptRef.current < MAX_RECONNECT_ATTEMPTS) {
                const delay = INITIAL_RECONNECT_DELAY * Math.pow(2, reconnectAttemptRef.current)
                reconnectAttemptRef.current++
                const seconds = delay / 1000
                const message = hasConnectedRef.current
                    ? i18n.common.connectionLostReconnecting({seconds})
                    : i18n.common.failedToConnectRetrying({seconds})
                setError(message)

                reconnectTimeoutRef.current = setTimeout(() => {
                    connect()
                }, delay)
            } else {
                const message = hasConnectedRef.current
                    ? i18n.common.connectionLostMaxAttempts
                    : i18n.common.failedToConnectLiveUpdates
                setError(message)
            }
        }

        const handleEvent = (event: MessageEvent) => {
            try {
                onEvent(JSON.parse(event.data))
            } catch (e) {
                console.error('Failed to parse SSE event:', e)
            }
        }

        eventTypes.forEach((type) => {
            eventSource.addEventListener(toSseEventName(type), handleEvent)
        })
    }, [enabled, url, eventTypes, onEvent, i18n])

    useEffect(() => {
        // Reset attempt counter per fresh subscription (mount / url-or-options change).
        // Keeping this out of connect() is load-bearing: the reconnect timer re-enters
        // connect(), so a reset inside would clobber the attempt count every retry and
        // MAX_RECONNECT_ATTEMPTS would never fire.
        reconnectAttemptRef.current = 0
        hasConnectedRef.current = false
        connect()
        return () => {
            disconnect()
        }
    }, [connect, disconnect])

    return {isConnected, error, disconnect}
}
